import { useEffect, useLayoutEffect, useRef, useState } from "react";

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDay(y, m)    { return new Date(y, m, 1).getDay(); }
function isSameDay(a, b) {
  return a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth()    === b.getMonth()    &&
    a.getDate()     === b.getDate();
}
function isToday(d) { return isSameDay(d, new Date()); }

// Display / typing format is now mm/dd/yyyy
function formatDisplay(d) {
  if (!d) return "";
  return `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}/${d.getFullYear()}`;
}

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function parseValue(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Parses a raw mm/dd/yyyy string (digits only, any separators) into a
// real Date, rejecting anything that isn't a calendar-valid date
// (e.g. 02/30/2026) instead of letting JS Date silently roll it over.
// Explicitly guards the "two-digit year" JS quirk (new Date(5,0,1) => 1905).
function parseTyped(str) {
  const digits = (str || "").replace(/\D/g, "");
  if (digits.length !== 8) return null;
  const mm = Number(digits.slice(0, 2));
  const dd = Number(digits.slice(2, 4));
  const yyyy = Number(digits.slice(4, 8));
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1000) return null;
  const d = new Date(0, 0, 1);
  d.setFullYear(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
}

// Auto-inserts the "/" separators as the user types digits, and clamps the
// month/day segments in real time (e.g. typing "18" for month snaps to
// "12") so an impossible value like 18/45/0000 can't be typed in the first
// place instead of only being rejected after the fact.
function maskInput(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  let mm = digits.slice(0, 2);
  let dd = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);

  if (mm.length === 2) {
    const n = Number(mm);
    if (n === 0) mm = "01";
    else if (n > 12) mm = "12";
  }
  if (dd.length === 2) {
    const n = Number(dd);
    if (n === 0) dd = "01";
    else if (n > 31) dd = "31";
  }

  if (digits.length > 4) return `${mm}/${dd}/${yyyy}`;
  if (digits.length > 2) return `${mm}/${dd}`;
  return mm;
}

export default function DatePicker(props) {
  const { value, onChange, name, label, disable, readOnly, inputRef, minDate, maxDate } = props;

  const today  = new Date();
  const parsed = parseValue(value);

  const [open,      setOpen]      = useState(false);
  const [viewMonth, setViewMonth] = useState((parsed || today).getMonth());
  const [viewYear,  setViewYear]  = useState((parsed || today).getFullYear());
  const [animDir,   setAnimDir]   = useState(null);
  const [rawInput,  setRawInput]  = useState(formatDisplay(parsed));
  const [invalid,   setInvalid]   = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const rootRef    = useRef(null);
  const internalTriggerRef = useRef(null);
  // The visible text input is now the focusable/typeable element, so
  // `inputRef` (the standard focus-handle prop across all Base fields)
  // resolves to it directly.
  const triggerRef = inputRef || internalTriggerRef;
  // Holds the caret position we want to restore after a masked re-render
  // (auto-inserting "/" would otherwise always bump the caret to the end).
  const pendingCaret = useRef(null);

  useLayoutEffect(() => {
    if (pendingCaret.current != null && triggerRef.current) {
      const pos = pendingCaret.current;
      triggerRef.current.setSelectionRange(pos, pos);
      pendingCaret.current = null;
    }
  }, [rawInput]);

  // Keep the field's text and the calendar view in sync whenever the
  // committed value changes from outside (parent re-render, form reset, etc).
  useEffect(() => {
    const d = parseValue(value);
    setRawInput(formatDisplay(d));
    setInvalid(false);
    if (d) { setViewMonth(d.getMonth()); setViewYear(d.getFullYear()); }
  }, [value]);

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Escape closes the sheet from anywhere, not just while the input is focused.
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  function navigate(dir) {
    setAnimDir(dir > 0 ? "left" : "right");
    setTimeout(() => setAnimDir(null), 280);
    let m = viewMonth + dir, y = viewYear;
    if (m > 11) { m = 0; y++; }
    if (m <  0) { m = 11; y--; }
    setViewMonth(m); setViewYear(y);
  }

  function commit(d) {
    if (minDate && d < minDate) return false;
    if (maxDate && d > maxDate) return false;
    onChange?.({ target: { name, value: toISO(d) } });
    setViewMonth(d.getMonth()); setViewYear(d.getFullYear());
    setInvalid(false);
    return true;
  }

  function selectDay(day) {
    const d = new Date(viewYear, viewMonth, day);
    if (!commit(d)) return;
    setTimeout(() => setOpen(false), 120);
  }

  function isDisabled(day) {
    const d = new Date(viewYear, viewMonth, day);
    return (minDate && d < minDate) || (maxDate && d > maxDate);
  }

  // --- Typing handlers -----------------------------------------------
  function handleInputChange(e) {
    const el = e.target;
    const digitsBeforeCursor = el.value.slice(0, el.selectionStart).replace(/\D/g, "").length;
    const masked = maskInput(el.value);

    // Figure out where the caret should land in the freshly-masked string:
    // right after the same number of digits it was after before masking.
    let caret = masked.length;
    if (digitsBeforeCursor === 0) {
      caret = 0;
    } else {
      let seen = 0;
      for (let i = 0; i < masked.length; i++) {
        if (/\d/.test(masked[i])) {
          seen++;
          if (seen === digitsBeforeCursor) { caret = i + 1; break; }
        }
      }
    }
    pendingCaret.current = caret;

    setRawInput(masked);
    setInvalid(false);
  }

  // Returns true if the field now holds a committed (or empty) value and
  // it's safe to close the popup / move focus away.
  function commitTyped() {
    if (rawInput.trim() === "") {
      if (parsed) onChange?.({ target: { name, value: "" } });
      setInvalid(false);
      return true;
    }
    const d = parseTyped(rawInput);
    if (!d || !commit(d)) {
      setInvalid(true);
      return false;
    }
    setRawInput(formatDisplay(d));
    return true;
  }

  function handleInputFocus() {
    setIsFocused(true);
  }

  function handleInputBlur() {
    setIsFocused(false);
    commitTyped();
  }

  function handleInputKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      const ok = commitTyped();
      if (ok) {
        setOpen(false);
      } else {
        // Keep focus in the field and select the text so the user can
        // immediately retype, instead of silently losing focus.
        e.target.select();
      }
    } else if (e.key === "Escape") {
      setRawInput(formatDisplay(parsed));
      setInvalid(false);
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!disable && !readOnly) setOpen(true);
    }
  }

  const cells = [];
  for (let i = 0; i < getFirstDay(viewYear, viewMonth); i++) cells.push(null);
  for (let d = 1; d <= getDaysInMonth(viewYear, viewMonth); d++) cells.push(d);

  // Inline styles so the field renders as one bordered row (icon + input +
  // chevron) even without matching CSS classes in the host stylesheet.
  const fieldStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    border: `1px solid ${invalid ? "#e0392c" : (isFocused || open) ? "#2f7bf5" : "#c9ced6"}`,
    borderRadius: "6px",
    padding: "0 10px",
    height: "38px",
    background: disable ? "#f4f5f7" : "#fff",
    boxShadow: invalid
      ? "0 0 0 3px rgba(224,57,44,0.15)"
      : (isFocused || open)
        ? "0 0 0 3px rgba(47,123,245,0.15)"
        : "none",
    transition: "border-color 120ms ease, box-shadow 120ms ease",
  };
  const iconStyle = { display: "flex", flexShrink: 0, color: "#8a8f98" };
  const inputStyle = {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    font: "inherit",
    fontSize: "14px",
    color: disable ? "#a7abb3" : "#2b2f36",
    padding: 0,
  };
  const arrowBtnStyle = {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    color: "#8a8f98",
    cursor: disable ? "default" : "pointer",
    padding: "4px",
    transform: open ? "rotate(180deg)" : "none",
    transition: "transform 150ms ease",
  };

  return (
    <div className="input-effect date-input" ref={rootRef}>
      {/* Hidden field carries the committed ISO value for native form submission */}
      <input type="hidden" name={name} value={value || ""} readOnly />

      {/* Field: typeable text input + calendar toggle, replacing the old button-only trigger */}
      <div
        className={`dp-field${open ? " dp-field--open" : ""}${invalid ? " dp-field--invalid" : ""}${disable ? " dp-field--disabled" : ""}`}
        style={fieldStyle}
      >
        <span className="dp-cal-icon" aria-hidden="true" style={iconStyle}>
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <rect x="1" y="2.5" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M1 6.5h14" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </span>

        <input
          ref={triggerRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={parsed || rawInput ? "dp-input dp-input--filled" : "dp-input"}
          style={inputStyle}
          placeholder="mm/dd/yyyy"
          value={rawInput}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          disabled={disable}
          readOnly={readOnly}
          maxLength={10}
          aria-invalid={invalid}
        />

        <button
          type="button"
          className={`dp-arrow-btn${open ? " dp-arrow-btn--up" : ""}`}
          style={arrowBtnStyle}
          onClick={() => { if (!disable && !readOnly) setOpen(o => !o); }}
          disabled={disable}
          tabIndex={-1}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Open calendar"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4.5l4 4 4-4" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {invalid && (
        <div style={{ fontSize: "12px", color: "#e0392c", marginTop: "4px" }}>
          Enter a valid date (mm/dd/yyyy)
        </div>
      )}

      <label className="input-effect-label">{label}</label>

      {/* Mobile Bottom Sheet */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="dp-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <div className="dp-sheet" role="dialog" aria-modal="true" aria-label="Date picker">
            {/* Drag handle */}
            <div className="dp-sheet-handle" />

            {/* Sheet Header */}
            <div className="dp-sheet-header">
              <span className="dp-sheet-label">{label || "Select Date"}</span>
              <button
                type="button"
                className="dp-sheet-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5"
                    strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Selected date display */}
            <div className="dp-selected-display">
              {parsed
                ? <><span className="dp-selected-day">{parsed.getDate()}</span>
                    <span className="dp-selected-month-year">{MONTHS[parsed.getMonth()]} {parsed.getFullYear()}</span>
                  </>
                : <span className="dp-selected-none">No date selected</span>
              }
            </div>

            {/* Month / Year navigation */}
            <div className="dp-nav-row">
              <button className="dp-nav-btn" type="button" onClick={() => navigate(-1)} aria-label="Previous month">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="dp-month-year">
                <select
                  className="dp-select"
                  value={viewMonth}
                  onChange={(e) => setViewMonth(Number(e.target.value))}
                  aria-label="Month"
                >
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i}>{m}</option>
                  ))}
                </select>
                <select
                  className="dp-select"
                  value={viewYear}
                  onChange={(e) => setViewYear(Number(e.target.value))}
                  aria-label="Year"
                >
                  {Array.from({ length: 20 }, (_, i) => {
                    const year = today.getFullYear() - 10 + i;
                    return <option key={year} value={year}>{year}</option>;
                  })}
                </select>
              </div>

              <button className="dp-nav-btn" type="button" onClick={() => navigate(1)} aria-label="Next month">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Grid */}
            <div className={`dp-grid${animDir ? ` dp-grid--${animDir}` : ""}`}>
              <div className="dp-weekdays">
                {DAYS.map(d => <span key={d}>{d}</span>)}
              </div>
              <div className="dp-days">
                {cells.map((day, i) =>
                  day === null
                    ? <span key={`e${i}`} className="dp-cell dp-cell--empty" />
                    : <button
                        key={day}
                        type="button"
                        className={[
                          "dp-cell",
                          isSameDay(new Date(viewYear, viewMonth, day), parsed) ? "dp-cell--sel"   : "",
                          isToday(new Date(viewYear, viewMonth, day))           ? "dp-cell--today" : "",
                          isDisabled(day)                                        ? "dp-cell--dis"   : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => selectDay(day)}
                        disabled={isDisabled(day)}
                      >
                        {day}
                      </button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="dp-footer">
              <button
                type="button"
                className="dp-btn-today"
                onClick={() => {
                  setViewMonth(today.getMonth());
                  setViewYear(today.getFullYear());
                  selectDay(today.getDate());
                }}
              >
                Today
              </button>
              {parsed && (
                <button
                  type="button"
                  className="dp-btn-clear"
                  onClick={() => { onChange?.({ target: { name, value: "" } }); setRawInput(""); setInvalid(false); setOpen(false); }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}